/**
 * Replay Controls - Bottom Playback Bar
 * Video-player-style controls for replay mode
 */
export class ReplayControls {
  constructor() {
    this.controlBar = null;
    this.isVisible = false;
    this.isPlaying = false;
    this.currentTime = 0;
    this.duration = 0;
    this.playbackSpeed = this.loadSavedPlaybackSpeed();
    this.isDragging = false;
    this.startTime = null; // Actual start time of replay data
    this.endTime = null;   // Actual end time of replay data
    this.isLiveMode = false;
    this.atLiveEdge = false;
    this.shareEnabled = false;
    this.sharePopoverOpen = false;
    this.includeCurrentReplayTimeInShareUrl = false;
    this.includeAlignStartsInShareUrl = false;
    this.alignStartsEnabled = false;
    this.referenceStartTime = null; // Set when align is active (for T+ display)

    this.init();
  }

  /**
   * Initialize the replay controls
   */
  init() {
    this.createControlBar();
    this.setupEventListeners();
    this.applyInitialPlaybackSpeed();
    this.updateModeActionButtons();
    this.updateLiveButtonState();
  }

  /**
   * Create the control bar DOM structure
   */
  createControlBar() {
    const controlBar = document.createElement('div');
    controlBar.className = 'replay-control-bar';
    controlBar.id = 'replay-control-bar';
    
    controlBar.innerHTML = `
      <div class="replay-controls-container">
        <!-- Left Section: Playback Controls -->
        <div class="replay-controls-left">
          <button class="replay-control-btn replay-skip-btn" id="replay-skip-backward" title="Skip -1 min">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 19l-7-7 7-7"/>
              <path d="M18 19l-7-7 7-7"/>
            </svg>
            <span class="skip-label">60</span>
          </button>
          
          <button class="replay-control-btn replay-play-btn" id="replay-play-pause" title="Play/Pause">
            <svg class="play-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
            <svg class="pause-icon hidden" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2">
              <rect x="6" y="4" width="4" height="16"/>
              <rect x="14" y="4" width="4" height="16"/>
            </svg>
          </button>
          
          <button class="replay-control-btn replay-skip-btn" id="replay-skip-forward" title="Skip +1 min">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M13 5l7 7-7 7"/>
              <path d="M6 5l7 7-7 7"/>
            </svg>
            <span class="skip-label">60</span>
          </button>
        </div>

        <!-- Center Section: Timeline Slider -->
        <div class="replay-controls-center">
          <div class="replay-time-display" id="replay-current-time">00:00:00</div>
          
          <div class="replay-timeline-container">
            <div class="replay-timeline" id="replay-timeline">
              <div class="replay-timeline-track"></div>
              <div class="replay-timeline-progress" id="replay-timeline-progress"></div>
              <div class="replay-timeline-buffer" id="replay-timeline-buffer"></div>
              
              <!-- Task markers on timeline -->
              <div class="replay-timeline-markers" id="replay-timeline-markers">
                <!-- Will be populated dynamically -->
              </div>
              
              <div class="replay-timeline-thumb" id="replay-timeline-thumb"></div>
              
              <!-- Hover tooltip -->
              <div class="replay-timeline-tooltip" id="replay-timeline-tooltip">
                <span class="tooltip-time">00:00:00</span>
              </div>
            </div>
          </div>
          
          <div class="replay-time-display" id="replay-total-time">00:00:00</div>
        </div>

        <!-- Right Section: Speed & Exit -->
        <div class="replay-controls-right">
          <div class="replay-speed-selector">
            <button class="replay-control-btn replay-speed-btn" id="replay-speed-btn" title="Playback Speed">
              <span class="speed-label" id="replay-speed-label">1x</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            <div class="replay-speed-menu" id="replay-speed-menu">
              <button class="replay-speed-option" data-speed="0.5">0.5x</button>
              <button class="replay-speed-option active" data-speed="1">1x</button>
              <button class="replay-speed-option" data-speed="2">2x</button>
              <button class="replay-speed-option" data-speed="5">5x</button>
              <button class="replay-speed-option" data-speed="10">10x</button>
              <button class="replay-speed-option" data-speed="20">20x</button>
            </div>
          </div>
          
          <button class="replay-control-btn replay-live-btn" id="replay-jump-live" title="At live edge">
            <span class="live-dot"></span>
            <span class="live-label">LIVE</span>
          </button>

          <button class="replay-control-btn replay-align-btn hidden" id="replay-align-starts" title="Align start times for comparison">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <line x1="4" y1="6" x2="20" y2="6"/>
              <line x1="4" y1="12" x2="20" y2="12"/>
              <line x1="4" y1="18" x2="20" y2="18"/>
              <circle cx="6" cy="6" r="2" fill="currentColor"/>
              <circle cx="12" cy="12" r="2" fill="currentColor"/>
              <circle cx="6" cy="18" r="2" fill="currentColor"/>
            </svg>
            <span class="align-label">Align</span>
          </button>

          <div class="replay-share-container hidden" id="replay-share-container">
            <button class="replay-control-btn replay-share-btn" id="replay-share-btn" title="Share replay">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/>
                <line x1="15.4" y1="6.5" x2="8.6" y2="10.5"/>
              </svg>
              <span class="share-label">Share</span>
            </button>
            <div class="replay-share-popover" id="replay-share-popover">
              <div class="replay-share-title">Share replay</div>
              <label class="replay-share-checkbox" for="replay-share-include-time">
                <input type="checkbox" id="replay-share-include-time" />
                <span>Start at current time</span>
              </label>
              <label class="replay-share-checkbox replay-share-align-starts-option hidden" for="replay-share-align-starts">
                <input type="checkbox" id="replay-share-align-starts" />
                <span>Align starts</span>
              </label>
              <div class="replay-share-url-row">
                <input type="text" class="replay-share-url" id="replay-share-url" readonly />
                <button class="replay-share-copy-btn" id="replay-share-copy-btn" type="button">Copy</button>
              </div>
              <div class="replay-share-status" id="replay-share-status" aria-live="polite"></div>
            </div>
          </div>

          <button class="replay-control-btn replay-exit-btn" id="replay-exit-btn" title="Exit Replay Mode">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            <span class="exit-label">Exit Replay</span>
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(controlBar);
    this.controlBar = controlBar;
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Play/Pause button
    const playPauseBtn = document.getElementById('replay-play-pause');
    if (playPauseBtn) {
      playPauseBtn.addEventListener('click', () => this.togglePlayPause());
    }

    // Skip buttons (1 minute = 60 seconds)
    const skipBackBtn = document.getElementById('replay-skip-backward');
    const skipForwardBtn = document.getElementById('replay-skip-forward');
    if (skipBackBtn) {
      skipBackBtn.addEventListener('click', () => this.skip(-60));
    }
    if (skipForwardBtn) {
      skipForwardBtn.addEventListener('click', () => this.skip(60));
    }

    // Timeline interaction - Mouse events
    const timeline = document.getElementById('replay-timeline');
    if (timeline) {
      timeline.addEventListener('mousedown', (e) => this.onTimelineMouseDown(e));
      timeline.addEventListener('mousemove', (e) => this.onTimelineMouseMove(e));
      timeline.addEventListener('mouseup', (e) => this.onTimelineMouseUp(e));
      timeline.addEventListener('mouseleave', () => this.hideTooltip());

      // Touch events for mobile
      timeline.addEventListener('touchstart', (e) => {
        e.preventDefault(); // Prevent scrolling while dragging
        const touch = e.touches[0];
        this.onTimelineMouseDown(touch);
      }, { passive: false });

      timeline.addEventListener('touchmove', (e) => {
        e.preventDefault();
        const touch = e.touches[0];
        if (this.isDragging) {
          this.onTimelineDrag(touch);
        }
      }, { passive: false });

      timeline.addEventListener('touchend', (e) => {
        if (this.isDragging) {
          this.isDragging = false;
        }
      });

      timeline.addEventListener('touchcancel', () => {
        this.isDragging = false;
      });
    }

    // Global mouse/touch events for dragging — store references for cleanup
    this._onDocMouseMove = (e) => {
      if (this.isDragging) {
        this.onTimelineDrag(e);
      }
    };
    this._onDocMouseUp = () => {
      if (this.isDragging) {
        this.isDragging = false;
        const timeline = document.getElementById('replay-timeline');
        if (timeline) {
          timeline.classList.remove('dragging');
        }
      }
    };
    this._onDocTouchMove = (e) => {
      if (this.isDragging) {
        const touch = e.touches[0];
        this.onTimelineDrag(touch);
      }
    };
    this._onDocTouchEnd = () => {
      if (this.isDragging) {
        this.isDragging = false;
        const timeline = document.getElementById('replay-timeline');
        if (timeline) {
          timeline.classList.remove('dragging');
        }
      }
    };
    document.addEventListener('mousemove', this._onDocMouseMove);
    document.addEventListener('mouseup', this._onDocMouseUp);
    document.addEventListener('touchmove', this._onDocTouchMove, { passive: false });
    document.addEventListener('touchend', this._onDocTouchEnd);

    // Speed selector
    const speedBtn = document.getElementById('replay-speed-btn');
    const speedMenu = document.getElementById('replay-speed-menu');
    if (speedBtn && speedMenu) {
      speedBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        speedMenu.classList.toggle('visible');
      });

      // Speed options
      const speedOptions = speedMenu.querySelectorAll('.replay-speed-option');
      speedOptions.forEach(option => {
        option.addEventListener('click', (e) => {
          const speed = parseFloat(e.target.dataset.speed);
          this.setPlaybackSpeed(speed);
          speedMenu.classList.remove('visible');
        });
      });

      // Close menu on outside click — store reference for cleanup
      this._onDocClick = () => {
        speedMenu.classList.remove('visible');
        this.hideSharePopover();
      };
      document.addEventListener('click', this._onDocClick);
    }

    // Jump to Live button
    const jumpLiveBtn = document.getElementById('replay-jump-live');
    if (jumpLiveBtn) {
      jumpLiveBtn.addEventListener('click', () => {
        if (!this.isLiveMode) {
          return;
        }
        this.emitEvent('jumpToLive');
      });
    }

    // Align Starts button
    const alignBtn = document.getElementById('replay-align-starts');
    if (alignBtn) {
      alignBtn.addEventListener('click', () => this.toggleAlignStarts());
    }

    const shareBtn = document.getElementById('replay-share-btn');
    const sharePopover = document.getElementById('replay-share-popover');
    const includeTimeCheckbox = document.getElementById('replay-share-include-time');
    const copyShareBtn = document.getElementById('replay-share-copy-btn');
    if (shareBtn) {
      shareBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!this.shareEnabled || this.isLiveMode) {
          return;
        }
        this.toggleSharePopover();
      });
    }
    if (sharePopover) {
      sharePopover.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }
    if (includeTimeCheckbox) {
      includeTimeCheckbox.checked = this.includeCurrentReplayTimeInShareUrl;
      includeTimeCheckbox.addEventListener('change', (e) => {
        this.includeCurrentReplayTimeInShareUrl = !!e.target.checked;
        this.setShareStatus('');
        this.updateShareUrlField();
      });
    }
    const alignStartsCheckbox = document.getElementById('replay-share-align-starts');
    if (alignStartsCheckbox) {
      alignStartsCheckbox.addEventListener('change', (e) => {
        this.includeAlignStartsInShareUrl = !!e.target.checked;
        this.setShareStatus('');
        this.updateShareUrlField();
      });
    }
    if (copyShareBtn) {
      copyShareBtn.addEventListener('click', () => this.copyShareUrl());
    }

    // Exit button
    const exitBtn = document.getElementById('replay-exit-btn');
    if (exitBtn) {
      exitBtn.addEventListener('click', () => this.exitReplay());
    }

    // Keyboard shortcuts — store reference for cleanup
    this._onDocKeyDown = (e) => {
      if (!this.isVisible) return;

      if (e.code === 'Space') {
        e.preventDefault();
        this.togglePlayPause();
      } else if (e.key === 'Escape' && this.sharePopoverOpen) {
        e.preventDefault();
        this.hideSharePopover();
      } else if (e.code === 'ArrowLeft') {
        e.preventDefault();
        this.skip(-60); // 1 minute back
      } else if (e.code === 'ArrowRight') {
        e.preventDefault();
        this.skip(60); // 1 minute forward
      }
    };
    document.addEventListener('keydown', this._onDocKeyDown);
  }

  /**
   * Show the control bar
   */
  show() {
    this.isVisible = true;
    this.controlBar.classList.add('visible');
    this.emitEvent('shown');
  }

  /**
   * Hide the control bar
   */
  hide() {
    this.isVisible = false;
    this.controlBar.classList.remove('visible');
    this.hideSharePopover();
    this.emitEvent('hidden');
  }

  /**
   * Remove all document-level event listeners to prevent leaks across replay sessions.
   */
  destroy() {
    if (this._onDocMouseMove) document.removeEventListener('mousemove', this._onDocMouseMove);
    if (this._onDocMouseUp) document.removeEventListener('mouseup', this._onDocMouseUp);
    if (this._onDocTouchMove) document.removeEventListener('touchmove', this._onDocTouchMove);
    if (this._onDocTouchEnd) document.removeEventListener('touchend', this._onDocTouchEnd);
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
    if (this._onDocKeyDown) document.removeEventListener('keydown', this._onDocKeyDown);
    this.hide();
  }

  /**
   * Toggle play/pause
   */
  togglePlayPause() {
    this.isPlaying = !this.isPlaying;
    
    const playIcon = this.controlBar.querySelector('.play-icon');
    const pauseIcon = this.controlBar.querySelector('.pause-icon');
    
    if (this.isPlaying) {
      playIcon.classList.add('hidden');
      pauseIcon.classList.remove('hidden');
    } else {
      playIcon.classList.remove('hidden');
      pauseIcon.classList.add('hidden');
    }

    this.emitEvent('playPauseToggled', { isPlaying: this.isPlaying });
  }

  /**
   * Skip forward or backward
   */
  skip(seconds) {
    this.currentTime = Math.max(0, Math.min(this.duration, this.currentTime + seconds));
    this.updateTimeDisplay();
    this.updateTimelinePosition();
    this.emitEvent('timeChanged', { currentTime: this.currentTime });
  }

  /**
   * Set playback speed
   */
  setPlaybackSpeed(speed) {
    this.playbackSpeed = speed;
    try {
      localStorage.setItem('condormap-replay-speed', String(speed));
    } catch (_) {}
    
    this.renderPlaybackSpeed(this.getEffectivePlaybackSpeed(), this.getEffectivePlaybackSpeed());

    this.emitEvent('speedChanged', { speed });
  }

  /**
   * Load saved playback speed (defaults to 20x for fresh sessions)
   */
  loadSavedPlaybackSpeed() {
    try {
      const saved = parseFloat(localStorage.getItem('condormap-replay-speed'));
      if (Number.isFinite(saved)) {
        return saved;
      }
    } catch (_) {}
    return 20;
  }

  /**
   * Apply initial playback speed to UI without emitting events
   */
  applyInitialPlaybackSpeed() {
    this.renderPlaybackSpeed(this.getEffectivePlaybackSpeed(), this.getEffectivePlaybackSpeed());
  }

  getEffectivePlaybackSpeed() {
    return (this.isLiveMode && this.atLiveEdge) ? 1.0 : this.playbackSpeed;
  }

  renderPlaybackSpeed(labelSpeed, activeSpeed) {
    const speedLabel = document.getElementById('replay-speed-label');
    if (speedLabel) {
      speedLabel.textContent = `${labelSpeed}x`;
    }

    const speedOptions = document.querySelectorAll('.replay-speed-option');
    speedOptions.forEach(option => {
      if (parseFloat(option.dataset.speed) === activeSpeed) {
        option.classList.add('active');
      } else {
        option.classList.remove('active');
      }
    });
  }

  /**
   * Timeline mouse down
   */
  onTimelineMouseDown(e) {
    this.isDragging = true;
    const timeline = document.getElementById('replay-timeline');
    if (timeline) {
      timeline.classList.add('dragging');
    }
    this.updateTimeFromMouse(e);
  }

  /**
   * Timeline mouse move (for hover tooltip)
   */
  onTimelineMouseMove(e) {
    if (!this.isDragging) {
      this.showTooltip(e);
    }
  }

  /**
   * Timeline mouse up
   */
  onTimelineMouseUp(e) {
    if (this.isDragging) {
      this.isDragging = false;
      const timeline = document.getElementById('replay-timeline');
      if (timeline) {
        timeline.classList.remove('dragging');
      }
      this.updateTimeFromMouse(e);
    }
  }

  /**
   * Timeline drag
   */
  onTimelineDrag(e) {
    this.updateTimeFromMouse(e);
  }

  /**
   * Update time from mouse position
   */
  updateTimeFromMouse(e) {
    const timeline = document.getElementById('replay-timeline');
    if (!timeline) return;

    const rect = timeline.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const percentage = x / rect.width;
    
    this.currentTime = percentage * this.duration;
    this.updateTimeDisplay();
    this.updateTimelinePosition();
    
    this.emitEvent('timeChanged', { currentTime: this.currentTime });
  }

  /**
   * Show tooltip on timeline hover
   */
  showTooltip(e) {
    const timeline = document.getElementById('replay-timeline');
    const tooltip = document.getElementById('replay-timeline-tooltip');
    if (!timeline || !tooltip) return;

    const rect = timeline.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const percentage = x / rect.width;
    const time = percentage * this.duration;

    tooltip.style.left = `${x}px`;
    tooltip.querySelector('.tooltip-time').textContent = this.formatTime(time);
    tooltip.classList.add('visible');
  }

  /**
   * Hide tooltip
   */
  hideTooltip() {
    const tooltip = document.getElementById('replay-timeline-tooltip');
    if (tooltip) {
      tooltip.classList.remove('visible');
    }
  }

  /**
   * Update time display
   * Left: Current local time
   * Right: Time remaining (countdown)
   */
  updateTimeDisplay() {
    const currentTimeEl = document.getElementById('replay-current-time');
    const totalTimeEl = document.getElementById('replay-total-time');
    
    if (currentTimeEl && this.startTime) {
      if (this.alignStartsEnabled && this.referenceStartTime) {
        // Show task-relative time: T+HH:MM:SS (seconds from reference start)
        const refOffsetSec = (this.referenceStartTime.getTime() - this.startTime.getTime()) / 1000;
        const taskTimeSec = this.currentTime - refOffsetSec;
        const prefix = taskTimeSec < 0 ? 'T-' : 'T+';
        currentTimeEl.textContent = prefix + this.formatTime(Math.abs(taskTimeSec));
      } else {
        // Show current local time (start time + current elapsed time)
        const currentClockTime = new Date(this.startTime.getTime() + this.currentTime * 1000);
        currentTimeEl.textContent = this.formatLocalTime(currentClockTime);
      }
    }
    if (totalTimeEl) {
      // Show time remaining (duration - current time)
      const remaining = Math.max(0, this.duration - this.currentTime);
      totalTimeEl.textContent = this.formatTime(remaining);
    }
  }

  /**
   * Update timeline position
   */
  updateTimelinePosition() {
    const progress = document.getElementById('replay-timeline-progress');
    const thumb = document.getElementById('replay-timeline-thumb');
    
    const percentage = this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
    
    if (progress) {
      progress.style.width = `${percentage}%`;
    }
    if (thumb) {
      thumb.style.left = `${percentage}%`;
    }
  }

  /**
   * Set duration and time range
   */
  setDuration(duration) {
    this.duration = duration;
    this.updateTimeDisplay();
  }

  /**
   * Set time range (start and end times)
   */
  setTimeRange(startTime, endTime) {
    this.startTime = startTime;
    this.endTime = endTime;
    this.updateTimeDisplay();
  }

  /**
   * Set current time
   */
  setCurrentTime(time) {
    this.currentTime = time;
    this.updateTimeDisplay();
    this.updateTimelinePosition();
    this.updateShareUrlField();
  }

  /**
   * Format time in HH:MM:SS (for elapsed time in tooltip)
   */
  formatTime(seconds) {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  /**
   * Format local time in 12-hour AM/PM format (uses browser's local timezone)
   */
  formatLocalTime(date) {
    let hrs = date.getHours();
    const mins = date.getMinutes();
    const secs = date.getSeconds();
    const ampm = hrs >= 12 ? 'PM' : 'AM';
    
    // Convert to 12-hour format
    hrs = hrs % 12;
    hrs = hrs ? hrs : 12; // 0 should be 12
    
    return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')} ${ampm}`;
  }

  /**
   * Toggle Align Starts mode
   */
  toggleAlignStarts() {
    this.alignStartsEnabled = !this.alignStartsEnabled;
    const btn = document.getElementById('replay-align-starts');
    if (btn) btn.classList.toggle('active', this.alignStartsEnabled);
    this.emitEvent('alignStartsToggled', { enabled: this.alignStartsEnabled });
  }

  /**
   * Reset Align Starts UI state (called when loading new data)
   */
  resetAlignStarts() {
    this.alignStartsEnabled = false;
    this.referenceStartTime = null;
    const btn = document.getElementById('replay-align-starts');
    if (btn) btn.classList.remove('active');
  }

  /**
   * Show or hide the Align Starts button
   */
  setAlignStartsVisible(visible) {
    const btn = document.getElementById('replay-align-starts');
    if (btn) btn.classList.toggle('hidden', !visible);
  }

  /**
   * Set reference start time for T+ display (called from main.js when alignment state changes)
   */
  setReferenceStartTime(time) {
    this.referenceStartTime = time;
  }

  /**
   * Exit replay mode
   */
  exitReplay() {
    this.emitEvent('exit');
  }

  /**
   * Add task marker to timeline
   */
  addTaskMarker(position, type = 'waypoint', label = '') {
    const markersContainer = document.getElementById('replay-timeline-markers');
    if (!markersContainer) return;

    const marker = document.createElement('div');
    marker.className = `replay-timeline-marker marker-${type}`;
    marker.style.left = `${position * 100}%`;
    marker.title = label;
    
    marker.innerHTML = `
      <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="10"/>
      </svg>
    `;
    
    markersContainer.appendChild(marker);
  }

  /**
   * Enable/disable live mode appearance.
   * Shows the LIVE badge state and hides the exit button in live-replay mode.
   */
  setLiveMode(isLive) {
    this.isLiveMode = isLive;
    const exitButton = document.getElementById('replay-exit-btn');
    const exitBtn = this.controlBar?.querySelector('.exit-label');

    if (isLive) {
      this.controlBar?.classList.add('live-mode');
      if (exitButton) exitButton.classList.add('hidden');
    } else {
      if (exitButton) exitButton.classList.remove('hidden');
      if (exitBtn) exitBtn.textContent = 'Exit Replay';
      this.controlBar?.classList.remove('live-mode');
      this.controlBar?.classList.remove('at-live-edge');
      this.atLiveEdge = false;
    }

    if (exitBtn && isLive) {
      exitBtn.textContent = 'Exit Replay';
    }

    this.renderPlaybackSpeed(this.getEffectivePlaybackSpeed(), this.getEffectivePlaybackSpeed());
    this.updateModeActionButtons();
    this.updateLiveButtonState();
  }

  setShareEnabled(isEnabled) {
    if (isEnabled && !this.shareEnabled) {
      this.includeCurrentReplayTimeInShareUrl = false;
      this.includeAlignStartsInShareUrl = false;
      const includeTimeCheckbox = document.getElementById('replay-share-include-time');
      if (includeTimeCheckbox) {
        includeTimeCheckbox.checked = false;
      }
      const alignStartsCheckbox = document.getElementById('replay-share-align-starts');
      if (alignStartsCheckbox) {
        alignStartsCheckbox.checked = false;
      }
    }

    this.shareEnabled = !!isEnabled;
    if (!this.shareEnabled) {
      this.hideSharePopover();
    }
    this.updateModeActionButtons();
    this.updateShareUrlField();
  }

  /**
   * Update whether playback is at the live edge.
   * Controls the pulsing dot and button styling.
   */
  setAtLiveEdge(atEdge) {
    const normalizedAtEdge = this.isLiveMode && !!atEdge;
    if (this.atLiveEdge === normalizedAtEdge) return;
    this.atLiveEdge = normalizedAtEdge;

    if (normalizedAtEdge) {
      this.controlBar?.classList.add('at-live-edge');
    } else {
      this.controlBar?.classList.remove('at-live-edge');
    }

    const effectiveSpeed = this.getEffectivePlaybackSpeed();
    this.renderPlaybackSpeed(effectiveSpeed, effectiveSpeed);
    this.emitEvent('liveEdgeStateChanged', {
      atEdge: this.atLiveEdge,
      preferredSpeed: this.playbackSpeed,
      effectiveSpeed
    });
    this.updateLiveButtonState();
  }

  updateLiveButtonState() {
    const jumpLiveBtn = document.getElementById('replay-jump-live');
    if (!jumpLiveBtn) {
      return;
    }

    const isInteractive = this.isLiveMode;
    jumpLiveBtn.classList.toggle('disabled', !isInteractive);
    jumpLiveBtn.setAttribute('aria-disabled', isInteractive ? 'false' : 'true');

    if (!isInteractive) {
      jumpLiveBtn.title = 'At live edge';
      return;
    }

    jumpLiveBtn.title = this.atLiveEdge ? 'At live edge' : 'Jump to live';
  }

  updateModeActionButtons() {
    const jumpLiveBtn = document.getElementById('replay-jump-live');
    const shareContainer = document.getElementById('replay-share-container');

    if (jumpLiveBtn) {
      jumpLiveBtn.classList.toggle('hidden', !this.isLiveMode);
    }

    if (shareContainer) {
      const showShare = this.shareEnabled && !this.isLiveMode;
      shareContainer.classList.toggle('hidden', !showShare);
      if (!showShare) {
        this.hideSharePopover();
      }
    }
  }

  toggleSharePopover() {
    if (this.sharePopoverOpen) {
      this.hideSharePopover();
    } else {
      this.showSharePopover();
    }
  }

  showSharePopover() {
    const sharePopover = document.getElementById('replay-share-popover');
    const shareUrl = document.getElementById('replay-share-url');
    if (!sharePopover) {
      return;
    }

    this.sharePopoverOpen = true;
    sharePopover.classList.add('visible');
    this.setShareStatus('');

    // Pre-check align-starts if currently active, and show/hide the option
    const alignStartsCheckbox = document.getElementById('replay-share-align-starts');
    const alignStartsLabel = alignStartsCheckbox?.closest('.replay-share-align-starts-option');
    const alignBtn = document.getElementById('replay-align-starts');
    const alignVisible = alignBtn && !alignBtn.classList.contains('hidden');
    if (alignStartsLabel) {
      alignStartsLabel.classList.toggle('hidden', !alignVisible);
    }
    if (alignStartsCheckbox && alignVisible) {
      this.includeAlignStartsInShareUrl = this.alignStartsEnabled;
      alignStartsCheckbox.checked = this.alignStartsEnabled;
    }

    this.updateShareUrlField();
    if (shareUrl) {
      shareUrl.focus();
      shareUrl.select();
    }
  }

  hideSharePopover() {
    const sharePopover = document.getElementById('replay-share-popover');
    if (!sharePopover) {
      this.sharePopoverOpen = false;
      return;
    }

    this.sharePopoverOpen = false;
    sharePopover.classList.remove('visible');
  }

  updateShareUrlField() {
    const shareUrl = document.getElementById('replay-share-url');
    if (!shareUrl) {
      return;
    }

    const url = new URL(window.location.href);
    if (this.includeCurrentReplayTimeInShareUrl) {
      url.searchParams.set('replayTime', String(Math.max(0, Math.floor(this.currentTime || 0))));
    } else {
      url.searchParams.delete('replayTime');
    }
    if (this.includeAlignStartsInShareUrl) {
      url.searchParams.set('alignStarts', '1');
    } else {
      url.searchParams.delete('alignStarts');
    }

    shareUrl.value = url.toString();
  }

  setShareStatus(message) {
    const status = document.getElementById('replay-share-status');
    if (status) {
      status.textContent = message;
    }
  }

  async copyShareUrl() {
    const shareUrl = document.getElementById('replay-share-url');
    if (!shareUrl) {
      return;
    }

    const fullUrl = shareUrl.value;
    let textToCopy = fullUrl;
    let statusMsg = 'Link copied';

    // Try URL shortener (LinkShrink — free, no auth, direct 301 redirects)
    try {
      const resp = await fetch('https://linkshrink.dev/api/v1/shorten', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: fullUrl }),
        signal: AbortSignal.timeout(4000),
      });
      if (resp.ok) {
        const data = await resp.json();
        const shortUrl = data?.data?.shortUrl;
        if (shortUrl && shortUrl.startsWith('http')) {
          textToCopy = shortUrl;
          statusMsg = 'Short link copied';
        }
      }
    } catch (_) {
      // CORS or network error — fall back to full URL
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        shareUrl.value = textToCopy;
        shareUrl.focus();
        shareUrl.select();
        document.execCommand('copy');
        shareUrl.value = fullUrl;
      }
      this.setShareStatus(statusMsg);
    } catch (_) {
      this.setShareStatus('Copy failed');
    }
  }

  /**
   * Event emission system
   */
  emitEvent(eventName, data = {}) {
    const event = new CustomEvent(`replayControls:${eventName}`, {
      detail: { ...data, controls: this }
    });
    document.dispatchEvent(event);
  }

  /**
   * Add event listener
   */
  addEventListener(eventName, callback) {
    document.addEventListener(`replayControls:${eventName}`, callback);
  }
}
